import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { Tag, Loader2 } from "lucide-react";

const ROWS = [
  ["A4", "80"], ["A4", "250"], ["A3", "80"], ["A3", "250"],
];
const PAPER_LABEL = { "80": "80gsm (normal)", "250": "250gsm (card stock)" };

export function PriceListDialog({ trigger, className }) {
  const [pricing, setPricing] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (open && !pricing) api.get("/pricing").then(({ data }) => setPricing(data));
  }, [open, pricing]);

  const sym = pricing?.currency_symbol || "€";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <button data-testid="view-pricelist" className={className || "inline-flex items-center gap-2 border-2 border-ink text-ink font-semibold px-6 py-3.5 rounded-lg hover:bg-ink hover:text-white transition-colors"}>
            <Tag size={18} /> View our price list
          </button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Our print pricing</DialogTitle>
        </DialogHeader>
        {!pricing ? (
          <div className="grid place-items-center py-12"><Loader2 className="animate-spin text-brand" size={26} /></div>
        ) : (
          <div data-testid="pricelist-content">
            <p className="text-sm text-slate-600 mb-4">Prices are <b>per page</b>. Your total is calculated as:</p>
            <div className="bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 font-mono text-sm text-ink mb-6">
              page price × pages × copies
              <div className="text-slate-500 text-xs mt-1 font-sans">Double-sided counts each printed side as a page (×{pricing.double_sided_multiplier}).</div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left uppercase text-xs tracking-[0.12em] text-slate-500 border-b border-slate-200">
                    <th className="py-2 pr-4 font-medium">Size</th>
                    <th className="py-2 pr-4 font-medium">Paper</th>
                    <th className="py-2 pr-4 font-medium">Mono</th>
                    <th className="py-2 font-medium">Colour</th>
                  </tr>
                </thead>
                <tbody>
                  {ROWS.map(([s, g]) => (
                    <tr key={s + g} className="border-b border-slate-100">
                      <td className="py-3 pr-4 font-semibold text-ink">{s}</td>
                      <td className="py-3 pr-4 text-slate-600">{PAPER_LABEL[g]}</td>
                      <td className="py-3 pr-4 font-mono text-ink">{sym}{Number(pricing[`price_${s.toLowerCase()}_${g}_bw`]).toFixed(2)}</td>
                      <td className="py-3 font-mono text-ink">{sym}{Number(pricing[`price_${s.toLowerCase()}_${g}_color`]).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-500 mt-6 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Prices shown online are an <b>estimate</b> based on the files you upload. The final price is confirmed in person when you collect your printing.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
